import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

// Address autocomplete backed by our own /api/address/* endpoints (Google
// Places API New, key held server-side). Two-step + session token for
// billing-efficient Places usage. onSelect contract is unchanged:
// { home_address, city, state, zip_code, country } (country '' when unknown:
// callers must not save an empty country).
export default function AddressAutocomplete({ value, onSelect, onManualChange, onBlur, id, name, className, placeholder, public: isPublic }) {
  const { API_URL } = useAuth();
  // Public (logged-out) forms hit the unauthenticated, rate-limited variants.
  const basePath = isPublic ? `${API_URL}/address/public` : `${API_URL}/address`;
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);
  const reqId = useRef(0);
  const sessionToken = useRef(null);

  const newToken = () =>
    (window.crypto?.randomUUID ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const getToken = useCallback(() => {
    if (!sessionToken.current) sessionToken.current = newToken();
    return sessionToken.current;
  }, []);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.trim().length < 3) {
      setSuggestions([]); setShowDropdown(false); return;
    }
    const myReq = ++reqId.current;
    try {
      const res = await axios.get(`${basePath}/autocomplete`, {
        params: { q: input, session_token: getToken() },
      });
      if (myReq !== reqId.current) return; // superseded by a newer keystroke
      const results = res.data?.suggestions || [];
      setSuggestions(results);
      setShowDropdown(results.length > 0);
      setHighlightedIndex(-1);
    } catch (err) {
      if (myReq !== reqId.current) return;
      console.error('Address autocomplete error:', err);
      setSuggestions([]); setShowDropdown(false);
    }
  }, [basePath, getToken]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (onManualChange) onManualChange({ target: { name: name || 'home_address', value: val } });
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (s) => {
    setShowDropdown(false);
    setSuggestions([]);
    const token = getToken();
    sessionToken.current = null; // selection ends the Places session
    try {
      const res = await axios.get(`${basePath}/details`, {
        params: { place_id: s.place_id, session_token: token },
      });
      const d = res.data || {};
      const streetAddress = d.street || d.formatted || s.primary || '';
      setQuery(streetAddress);
      if (onSelect) {
        onSelect({
          home_address: streetAddress,
          city: d.city || '',
          state: d.state || '',
          zip_code: d.zip || '',
          country: d.country || '',
        });
      }
    } catch (err) {
      console.error('Address details error:', err);
      // Fall back to the prediction's primary text as the street line.
      const fallback = s.primary || s.description || '';
      setQuery(fallback);
      if (onSelect) onSelect({ home_address: fallback, city: '', state: '', zip_code: '', country: '' });
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex(p => (p < suggestions.length - 1 ? p + 1 : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex(p => (p > 0 ? p - 1 : suggestions.length - 1)); }
    else if (e.key === 'Enter' && highlightedIndex >= 0) { e.preventDefault(); handleSelect(suggestions[highlightedIndex]); }
    else if (e.key === 'Escape') { setShowDropdown(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        name={name || 'home_address'}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        className={className}
        placeholder={placeholder}
        autoComplete="off"
      />
      {showDropdown && suggestions.length > 0 && (
        <div ref={dropdownRef} className="school-autocomplete-dropdown">
          {suggestions.map((s, idx) => (
            <div
              key={s.place_id || idx}
              className={`school-autocomplete-item ${idx === highlightedIndex ? 'school-autocomplete-item--active' : ''}`}
              onMouseDown={() => handleSelect(s)}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              <div className="school-autocomplete-name">{s.primary || s.description}</div>
              <div className="school-autocomplete-location">{s.secondary}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
